import { motion } from 'framer-motion';
import { ArrowRight, Play, Check, MapPin, TrendingUp, Sparkles } from 'lucide-react';
import { Container } from './shared';

const KPIS = [
  { label: 'ACTIVE USERS', value: '247', delta: '+12%', up: true },
  { label: 'OPEN COMPLAINTS', value: '7', delta: '-23%', up: true },
  { label: 'PIPELINE', value: '₹4.2Cr', delta: '+28%', up: true },
  { label: 'REPORTS TODAY', value: '96%', delta: '+4%', up: true },
];
const BARS = [40, 55, 48, 70, 62, 85, 74, 90, 68, 95, 80, 100];

const DashboardMockup = () => (
  <div className="ld-mockup p-4 sm:p-5" data-testid="dashboard-mockup">
    <div className="flex items-center justify-between mb-4">
      <div className="flex gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" /><span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" /><span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" /></div>
      <span className="ld-mono text-[10px] tracking-[0.2em] text-slate-500">WOXORA.APP / DASHBOARD</span>
      <span className="flex items-center gap-1.5 text-[10px] text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 ld-pulse-dot" /> LIVE</span>
    </div>
    <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-3">
      {KPIS.map((k) => (
        <div key={k.label} className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-2.5 sm:p-3">
          <p className="ld-mono text-[8px] sm:text-[9px] tracking-[0.15em] text-slate-500 truncate">{k.label}</p>
          <p className="ld-mono text-sm sm:text-lg font-semibold text-slate-100 mt-1">{k.value}</p>
          <p className="text-[10px] text-emerald-400">{k.delta}</p>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-5 gap-3">
      <div className="col-span-3 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3">
        <p className="ld-mono text-[9px] tracking-[0.15em] text-slate-500 mb-3">SUBMISSIONS · 12 DAYS</p>
        <div className="flex items-end gap-1.5 h-24">
          {BARS.map((h, i) => <div key={i} className="ld-bar flex-1" style={{ height: `${h}%`, animationDelay: `${i * 60}ms` }} />)}
        </div>
      </div>
      <div className="col-span-2 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3 space-y-3">
        <p className="ld-mono text-[9px] tracking-[0.15em] text-slate-500">RESOLUTION</p>
        {[['Resolved', 72, '#22C55E'], ['In progress', 48, '#E06D53'], ['Pending', 20, '#38BDF8']].map(([l, v, c]) => (
          <div key={l}>
            <div className="flex justify-between text-[10px] text-slate-400 mb-1"><span>{l}</span><span className="ld-mono">{v}%</span></div>
            <div className="h-1 rounded bg-white/5"><div className="h-1 rounded" style={{ width: `${v}%`, background: c }} /></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Float = ({ className, icon: Icon, color, text, testId }) => (
  <div className={`absolute ld-glass rounded-xl px-3.5 py-2.5 flex items-center gap-2 text-xs font-medium text-slate-100 shadow-2xl ${className}`} data-testid={testId}>
    <Icon className="w-3.5 h-3.5" style={{ color }} /> {text}
  </div>
);

export const Hero = ({ onStart, onDemo }) => (
  <section className="relative overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 ld-grid-bg" />
    <div className="absolute inset-0 ld-spot" />
    <Container className="relative pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
      <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="ld-overline" data-testid="hero-overline">AI-powered business operations platform</p>
        <h1 className="ld-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] text-slate-50 mt-6" data-testid="hero-heading">
          Run your entire business operations <span className="text-[#E06D53]">on autopilot.</span>
        </h1>
        <p className="text-base lg:text-lg leading-relaxed text-slate-400 mt-6 max-w-xl" data-testid="hero-description">
          Daily reports, sales pipelines, complaints, GPS attendance and tasks — unified in one AI-driven workspace built for Indian enterprises.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-10">
          <button onClick={onStart} className="ld-btn-primary text-base !px-7 !py-3.5" data-testid="hero-get-started-cta">Start free <ArrowRight className="w-4 h-4" /></button>
          <button onClick={onDemo} className="ld-btn-ghost text-base !px-7 !py-3.5" data-testid="hero-watch-demo-btn"><Play className="w-4 h-4" /> Book a 1-on-1 demo</button>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm text-slate-500" data-testid="hero-trust-points">
          {['No credit card', '5-minute setup', 'Cancel anytime'].map((t) => <span key={t} className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" />{t}</span>)}
        </div>
      </motion.div>
      <motion.div className="lg:col-span-6 relative" initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.9, delay: 0.15 }}>
        <div className="absolute -inset-8 bg-[radial-gradient(circle_at_50%_50%,rgba(224,109,83,0.18),transparent_60%)] blur-2xl" />
        <div className="relative">
          <DashboardMockup />
          <Float className="-top-5 -left-2 sm:-left-8 ld-float-1" icon={Sparkles} color="#E06D53" text="AI report auto-generated" testId="floating-card-0" />
          <Float className="top-1/3 -right-2 sm:-right-8 ld-float-2" icon={TrendingUp} color="#22C55E" text="Lead closed · ₹1,20,000" testId="floating-card-1" />
          <Float className="-bottom-5 left-6 sm:left-12 ld-float-3" icon={MapPin} color="#38BDF8" text="GPS attendance verified" testId="floating-card-2" />
        </div>
      </motion.div>
    </Container>
  </section>
);
